import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { PublicPageWrapper } from './components/PublicPageWrapper';
import i18n from './i18n';
import { resolveInitialLanguage, SUPPORTED_LANGUAGES } from './i18n/languages';
import { captureAdsAttribution, captureCampaignAttribution } from './utils/stripeCheckoutUrl';
import { Loader2 } from 'lucide-react';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { AccountProvider } from './contexts/AccountContext';
import { AccountSyncProvider } from './contexts/AccountSyncContext';
import { SimplifiedStatsProvider } from './contexts/SimplifiedStatsContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import { ToastProvider } from './contexts/ToastContext';
import { TradingAccountProvider } from './contexts/TradingAccountContext';
import { AIAnalysisProvider } from './contexts/AIAnalysisContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AnnouncementProvider, useAnnouncement } from './contexts/AnnouncementContext';
import { ActivationChecklistProvider, useActivationChecklist } from './contexts/ActivationChecklistContext';
import { OnboardingDemoProvider } from './contexts/OnboardingDemoContext';
import { DisplayFormatProvider } from './contexts/DisplayFormatContext';
import { PrivateRoute } from './components/PrivateRoute';
import { TrialDemarrageGuard } from './components/TrialDemarrageGuard';
import { PrivateProRoute } from './components/PrivateProRoute';
import { PrivatePremiumOnlyRoute } from './components/PrivatePremiumOnlyRoute';
import { AdminRoute } from './components/AdminRoute';
import { RedactorRoute } from './components/RedactorRoute';
import { OnboardingRoute } from './components/OnboardingRoute';
import AuthRedirect from './components/AuthRedirect';
import { ScrollToTop } from './components/ScrollToTop';
import { GoogleAnalyticsTracker } from './components/GoogleAnalyticsTracker';
import { PostHogTracker } from './components/PostHogTracker';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './lib/firebase';

// ===== LAZY: tout ce qui ne doit PAS charger sur la landing =====
const Layout = lazy(() => import('./components/Layout').then(m => ({ default: m.Layout })));
const LandingPage = lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })));
const LandingPageBeta = lazy(() => import('./pages/LandingPageBeta').then(m => ({ default: m.LandingPage })));
const LandingPageOriginal = lazy(() => import('./pages/LandingPageOriginal').then(m => ({ default: m.LandingPage })));
const LoginForm = lazy(() => import('./components/auth/LoginForm').then(m => ({ default: m.LoginForm })));
const RegisterForm = lazy(() => import('./components/auth/RegisterForm').then(m => ({ default: m.RegisterForm })));
const Onboarding = lazy(() => import('./pages/Onboarding').then(m => ({ default: m.Onboarding })));
const EmailVerificationHandler = lazy(() => import('./components/auth/EmailVerificationHandler').then(m => ({ default: m.EmailVerificationHandler })));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({ default: m.default })));
const AffiliateOnboardingManager = lazy(() => import('./components/AffiliateOnboardingManager').then(m => ({ default: m.AffiliateOnboardingManager })));
const ColorThemeSelectionModal = lazy(() => import('./components/modals/ColorThemeSelectionModal').then(m => ({ default: m.ColorThemeSelectionModal })));
const ThemeEditorPanel = lazy(() => import('./components/modals/ThemeEditorPanel').then(m => ({ default: m.ThemeEditorPanel })));
const TimezoneChangeModal = lazy(() => import('./components/modals/TimezoneChangeModal').then(m => ({ default: m.TimezoneChangeModal })));
// ===== PAGES PUBLIQUES (pour tous) =====
const ForgotPasswordForm = lazy(() => import('./components/auth/ForgotPasswordForm').then(module => ({ default: module.ForgotPasswordForm })));
const ResetPasswordForm = lazy(() => import('./components/auth/ResetPasswordForm').then(module => ({ default: module.ResetPasswordForm })));
const EmailVerification = lazy(() => import('./pages/EmailVerification').then(module => ({ default: module.EmailVerification })));
const EmailValidationForm = lazy(() => import('./components/auth/EmailValidationForm').then(module => ({ default: module.EmailValidationForm })));
// const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const ContactUs = lazy(() => import('./pages/ContactUs').then(module => ({ default: module.ContactUs })));
const Pricing = lazy(() => import('./pages/Pricing').then(module => ({ default: module.Pricing })));
const PricingNew = lazy(() => import('./pages/PricingNew').then(module => ({ default: module.Pricing })));
const DirectPricing = lazy(() => import('./pages/DirectPricing').then(module => ({ default: module.Pricing })));
const Offer = lazy(() => import('./pages/Offer').then(module => ({ default: module.Offer })));
const Features = lazy(() => import('./pages/Features').then(module => ({ default: module.Features })));
const ChangelogPage = lazy(() => import('./pages/Changelog').then(module => ({ default: module.ChangelogPage })));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(module => ({ default: module.TermsOfService })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));
const SecurityPolicy = lazy(() => import('./pages/SecurityPolicy').then(module => ({ default: module.SecurityPolicy })));
const AffiliateTerms = lazy(() => import('./pages/AffiliateTerms').then(module => ({ default: module.AffiliateTerms })));
const Success = lazy(() => import('./pages/Success').then(module => ({ default: module.default })));
const Welcome = lazy(() => import('./pages/Welcome').then(module => ({ default: module.Welcome })));
const WelcomeA = lazy(() => import('./pages/WelcomeA').then(module => ({ default: module.WelcomeA })));
const WelcomeM = lazy(() => import('./pages/WelcomeM').then(module => ({ default: module.WelcomeM })));
const EmailVerified = lazy(() => import('./pages/EmailVerified').then(module => ({ default: module.EmailVerified })));
const EmailConfirmed = lazy(() => import('./pages/EmailConfirmed').then(module => ({ default: module.EmailConfirmed })));
const BrokerList = lazy(() => import('./pages/BrokerList').then(module => ({ default: module.BrokerList })));
const Disclaimer = lazy(() => import('./pages/Disclaimer').then(module => ({ default: module.Disclaimer })));
const HelpCenter = lazy(() => import('./pages/HelpCenter').then(module => ({ default: module.HelpCenter })));
const HelpCategory = lazy(() => import('./pages/HelpCategory').then(module => ({ default: module.HelpCategory })));
const HelpArticle = lazy(() => import('./pages/HelpArticle').then(module => ({ default: module.HelpArticle })));
const SharedTrades = lazy(() => import('./pages/SharedTrades').then(module => ({ default: module.SharedTrades })));
const ZeFrenchTrader = lazy(() => import('./pages/ZeFrenchTrader').then(module => ({ default: module.default })));
const AdminAuth = lazy(() => import('./pages/AdminAuth').then(module => ({ default: module.AdminAuth })));
const Essai = lazy(() => import('./pages/Essai').then(module => ({ default: module.default })));
const NewsletterUnsubscribe = lazy(() => import('./pages/NewsletterUnsubscribe').then(module => ({ default: module.default })));
const CTraderLogout = lazy(() => import('./pages/CTraderLogout').then(module => ({ default: module.CTraderLogout })));
const BlackFriday = lazy(() => import('./pages/Blackfriday').then(module => ({ default: module.default })));
const BlackFridayTemp = lazy(() => import('./pages/BlackFridayTemp').then(module => ({ default: module.default })));
// `pages/en/LandingPageBeta` n'est plus routé : `/en` sert désormais la landing
// traduite via i18next, comme les cinq autres langues. Le fichier est conservé.

// ===== PAGES UTILISATEURS GRATUITS/PREMIUM =====
const Demarrage = lazy(() => import('./pages/Demarrage').then(module => ({ default: module.Demarrage })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const NewTrade = lazy(() => import('./pages/NewTrade').then(module => ({ default: module.NewTrade })));
const Journal = lazy(() => import('./pages/Journal').then(module => ({ default: module.Journal })));
const PositionCalculator = lazy(() => import('./pages/PositionCalculator').then(module => ({ default: module.PositionCalculator })));
const Analytics = lazy(() => import('./pages/Analytics').then(module => ({ default: module.Analytics })));
const Settings = lazy(() => import('./pages/Settings').then(module => ({ default: module.Settings })));
const Profile = lazy(() => import('./pages/Profile').then(module => ({ default: module.Profile })));
const PrivacyCenter = lazy(() => import('./pages/PrivacyCenter').then(module => ({ default: module.PrivacyCenter })));
const Calendar = lazy(() => import('./pages/Calendar').then(module => ({ default: module.Calendar })));
const Partners = lazy(() => import('./pages/Partners').then(module => ({ default: module.Partners })));
const Achievements = lazy(() => import('./pages/Achievements').then(module => ({ default: module.Achievements })));

// ===== PAGES PREMIUM UNIQUEMENT =====
const Tags = lazy(() => import('./pages/Tags').then(module => ({ default: module.Tags })));
const Plans = lazy(() => import('./pages/Plans').then(module => ({ default: module.Plans })));
const Retails = lazy(() => import('./pages/Retails').then(module => ({ default: module.Retails })));
const Seasonality = lazy(() => import('./pages/Seasonality').then(module => ({ default: module.Seasonality })));
const IAAnalysis = lazy(() => import('./pages/IAAnalysis').then(module => ({ default: module.IAAnalysis })));
const PlanVsRealite = lazy(() => import('./pages/PlanVsRealite').then(module => ({ default: module.PlanVsRealite })));
const Discipline = lazy(() => import('./pages/Discipline').then(module => ({ default: module.Discipline })));
const Reports = lazy(() => import('./pages/Reports').then(module => ({ default: module.Reports })));
const Playbook = lazy(() => import('./pages/Playbook').then(module => ({ default: module.Playbook })));
const PropFirmCockpit = lazy(() => import('./pages/PropFirmCockpit').then(module => ({ default: module.PropFirmCockpit })));
const PropFirmChallenges = lazy(() => import('./pages/PropFirmChallenges').then(module => ({ default: module.PropFirmChallenges })));
const PropFirmSimulator = lazy(() => import('./pages/PropFirmSimulator').then(module => ({ default: module.PropFirmSimulator })));
const Notes = lazy(() => import('./pages/Notes').then(module => ({ default: module.Notes })));
const Backtest = lazy(() => import('./pages/Backtest').then(module => ({ default: module.Backtest })));
const BacktestSession = lazy(() => import('./pages/BacktestSession').then(module => ({ default: module.BacktestSessionPage })));
const Affiliate = lazy(() => import('./pages/Affiliate').then(module => ({ default: module.Affiliate })));
const AffiliatePage = lazy(() => import('./pages/AffiliatePage').then(module => ({ default: module.AffiliatePage })));
const BacktestingPage = lazy(() => import('./pages/BacktestingPage').then(module => ({ default: module.BacktestingPage })));
const AffiliateLogin = lazy(() => import('./pages/AffiliateLogin').then(module => ({ default: module.AffiliateLogin })));
const AffiliateRegister = lazy(() => import('./pages/AffiliateRegister').then(module => ({ default: module.AffiliateRegister })));
const AffiliateDashboard = lazy(() => import('./pages/AffiliateDashboard').then(module => ({ default: module.AffiliateDashboard })));
const PartnerDashboard = lazy(() => import('./pages/PartnerPage').then(module => ({ default: module.PartnerDashboard })));
const PartnersLogin = lazy(() => import('./pages/PartnersLogin').then(module => ({ default: module.PartnersLogin })));
const PartnersRegister = lazy(() => import('./pages/PartnersRegister').then(module => ({ default: module.PartnersRegister })));
const PartnersProgram = lazy(() => import('./pages/PartnersPage').then(module => ({ default: module.PartnersProgram })));
const OurPartnersPage = lazy(() => import('./pages/OurPartnersPage').then(module => ({ default: module.OurPartnersPage })));

// ===== PAGES COMMUNAUTÉ =====
const Community = lazy(() => import('./pages/Community').then(module => ({ default: module.Community })));
const Leaderboard = lazy(() => import('./pages/Leaderboard').then(module => ({ default: module.Leaderboard })));

// ===== PAGES BLOG =====
const Blog = lazy(() => import('./pages/Blog').then(module => ({ default: module.default })));
const BlogArticle = lazy(() => import('./pages/BlogArticle').then(module => ({ default: module.default })));
const AdminBlog = lazy(() => import('./pages/AdminBlog').then(module => ({ default: module.default })));

// ===== PAGES ACADEMY =====
const Academy = lazy(() => import('./pages/Academy').then(module => ({ default: module.default })));
const AcademyTemp = lazy(() => import('./pages/AcademyTemp').then(module => ({ default: module.default })));
const JoinAcademy = lazy(() => import('./pages/JoinAcademy').then(module => ({ default: module.default })));
const JoinBetatester = lazy(() => import('./pages/JoinBetatester').then(module => ({ default: module.default })));

// ===== PAGES ADMIN (SÉPARÉES - NE SE CHARGENT QUE POUR LES ADMINS) =====
// Ces imports ne sont même PAS définis pour les utilisateurs normaux
// Ils seront chargés dynamiquement seulement si nécessaire
const AdminRoutes = lazy(() => import('./components/AdminRoutes.tsx'));
const AdminStatsPage = lazy(() => import('./pages/AdminStatsPage'));

// Composant de chargement élégant
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-background-primary to-background-secondary flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
        <div className="absolute inset-0 h-8 w-8 animate-ping rounded-full bg-brand-primary/20"></div>
      </div>
      <p className="text-content-secondary text-sm animate-pulse">
        Chargement en cours...
      </p>
    </div>
  </div>
);

// Composant de chargement plus rapide pour les pages simples
// const QuickLoader = () => (
//   <div className="flex items-center justify-center py-8">
//     <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
//   </div>
// );

// Import du script de test en développement
if (process.env.NODE_ENV === 'development') {
  // @ts-ignore
  import('./utils/testGoogleAuth.js').catch(console.warn);
}

// Composant pour gérer les modals d'annonce
const ThemeEditorManager = () => {
  const location = useLocation();
  const themeEditor = new URLSearchParams(location.search).get('themeEditor');
  if (!themeEditor) return null;
  return (
    <Suspense fallback={null}>
      <ThemeEditorPanel editId={themeEditor} />
    </Suspense>
  );
};

// Garde générique pour les fonctionnalités encore réservées aux bêta-testeurs
// (achievements, prop firm...). Redirige tout le monde d'autre vers /dashboard,
// avec un fallback Firestore au cas où le claim isBetatesteur du contexte Auth
// n'est pas encore synchronisé.
// `allowPremium` : la fonctionnalité prop firm est sortie de bêta pour les comptes
// premium (hors offre Basic) — voir canAccessPropFirmBeta() dans firestore.rules,
// qui doit rester en accord avec ce garde côté client.
const BetaFeatureRoute = ({ children, allowPremium = false }: { children: React.ReactNode; allowPremium?: boolean }) => {
  const { loading, user, isAdmin, isBetatesteur, isPremium, isBasic } = useAuth();
  const [hasBetaFallbackAccess, setHasBetaFallbackAccess] = useState(false);
  const [isCheckingBetaFallback, setIsCheckingBetaFallback] = useState(false);

  useEffect(() => {
    if (isAdmin || isBetatesteur) {
      setHasBetaFallbackAccess(true);
      setIsCheckingBetaFallback(false);
      return;
    }

    if (!user?.uid) {
      setHasBetaFallbackAccess(false);
      setIsCheckingBetaFallback(false);
      return;
    }

    let cancelled = false;
    setIsCheckingBetaFallback(true);

    (async () => {
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const userData = userSnap.exists() ? userSnap.data() as Record<string, unknown> : null;
        if (!cancelled) {
          setHasBetaFallbackAccess(userData?.isBetatesteur === true || userData?.isBetatesteur === 'true');
        }
      } catch {
        if (!cancelled) {
          setHasBetaFallbackAccess(false);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingBetaFallback(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, isBetatesteur, user?.uid]);

  const canAccessBetaFeature =
    isAdmin || isBetatesteur || hasBetaFallbackAccess || (allowPremium && isPremium && !isBasic);

  if (loading || isCheckingBetaFallback) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!canAccessBetaFeature) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const AnnouncementModalManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    showLightModeModal, 
    setShowLightModeModal,
    dismissLightModeAnnouncement, 
    markLightModeAnnouncementAsSeen,
    showColorThemeModal,
    setShowColorThemeModal,
    dismissColorThemeAnnouncement,
    markColorThemeAnnouncementAsSeen
  } = useAnnouncement();
  const { markPersonnalisationDone, checklistState } = useActivationChecklist();

  // Ouvrir le modal de personnalisation si la page est atteinte avec ?onboardingStep=personnalisation
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (location.pathname === '/dashboard' && params.get('onboardingStep') === 'personnalisation') {
      setShowColorThemeModal(true);
    }
  }, [location.pathname, location.search, setShowColorThemeModal]);

  // Pages où les modals ne doivent PAS s'afficher
  const excludedPaths = [
    '/',
    '/pricing',
    '/offre',
    '/affiliate',
    '/affiliate/dashboard',
    '/security',
    '/terms',
    '/blog',
    '/contact',
    '/help',
    '/plateformes',
    '/privacy',
    '/affiliate-terms',
    '/changelog',
    '/login',
    '/register',
    '/welcome',
    '/welcome-a',
    '/welcome-m',
    '/affiliation',
    '/onboarding',
  ];

  const currentPath = window.location.pathname;
  
  // Vérifier si on est sur une page exclue ou une sous-page (ex: /blog/article-1, /help/category)
  const isExcludedPage = excludedPaths.some(path => 
    currentPath === path || currentPath.startsWith(path + '/')
  );

  // Le modal de personnalisation du tutoriel doit s'afficher uniquement sur /dashboard.
  const isDashboardPage = currentPath === '/dashboard';

  return (
    <>
      {!isExcludedPage && (
        <>
          <Suspense fallback={null}><TimezoneChangeModal /></Suspense>
        </>
      )}
      {isDashboardPage && (
        <Suspense fallback={null}><ColorThemeSelectionModal
          isOpen={showColorThemeModal || showLightModeModal}
          onClose={() => {
            const shouldReturnToDemarrage = checklistState.personnalisation !== 'done' && location.pathname === '/dashboard';
            setShowColorThemeModal(false);
            setShowLightModeModal(false);
            markColorThemeAnnouncementAsSeen();
            markLightModeAnnouncementAsSeen();
            markPersonnalisationDone();
            if (shouldReturnToDemarrage) {
              navigate('/demarrage');
            }
          }}
          onDismiss={() => {
            setShowColorThemeModal(false);
            setShowLightModeModal(false);
            dismissColorThemeAnnouncement();
            dismissLightModeAnnouncement();
          }}
        /></Suspense>
      )}
    </>
  );
};

// Wrapper pour la landing page qui gère l'affichage conditionnel
const LandingPageWrapper = () => {
  return <PublicPageWrapper><LandingPage /></PublicPageWrapper>;
};

/**
 * Landing servie sous un préfixe de langue (`/fr`, `/en`, `/es`, `/it`, `/de`, `/pt`).
 * Le segment d'URL fait autorité : il aligne i18next dessus au montage, pour qu'un
 * lien partagé s'ouvre toujours dans la bonne langue.
 */
const LocalizedLandingRoute = ({ language }: { language: string }) => {
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language]);

  return <PublicPageWrapper><LandingPageBeta /></PublicPageWrapper>;
};

/**
 * Racine du site : redirige vers la landing dans la langue détectée
 * (URL → choix mémorisé → `navigator.languages` → français).
 */
const RootLanguageRedirect = () => {
  // Capture gclid/gbraid/wbraid avant toute redirection : c'est le seul point
  // de passage garanti pour tout visiteur qui atterrit sur le domaine racine
  // (y compris les clics Google Ads, qui pointent vers "/").
  captureAdsAttribution(); // avant captureCampaignAttribution, qui retire les UTM de l'URL
  captureCampaignAttribution();
  return <Navigate to={`/${resolveInitialLanguage()}`} replace />;
};

// Capture aussi les campagnes (UTM + gclid/gbraid/wbraid) qui arrivent directement
// sur une route localisée ou une page d'inscription, sans dépendre de la redirection racine.
const CampaignAttributionTracker = () => {
  const location = useLocation();
  useEffect(() => {
    // Ordre important : captureCampaignAttribution retire utm_source/utm_campaign de l'URL.
    captureAdsAttribution();
    captureCampaignAttribution();
  }, [location.search]);
  return null;
};

// Wrapper pour la page Black Friday avec vérification de date
const BlackFridayWrapper = () => {
  const now = new Date();
  const startDate = new Date('2025-11-22T00:00:00+01:00');
  const endDate = new Date('2025-11-30T23:59:00+01:00');
  
  // Rediriger vers la page d'accueil si hors période Black Friday
  if (now < startDate || now > endDate) {
    window.location.href = '/';
    return null;
  }
  
  return <PublicPageWrapper><BlackFriday /></PublicPageWrapper>;
};

function App() {

  return (
    <AuthProvider>
      <ThemeProvider>
        <AnnouncementProvider>
          <SubscriptionProvider>
          <UserPreferencesProvider>
            <TradingAccountProvider>
              <SimplifiedStatsProvider>
              <AccountProvider>
                <AccountSyncProvider>
                <ActivationChecklistProvider>
                <DisplayFormatProvider>
                  <ToastProvider>
                    <Router>
                      <CampaignAttributionTracker />
                      <OnboardingDemoProvider>
                      <ErrorBoundary>
                      <ScrollToTop />
                      <GoogleAnalyticsTracker />
                      <PostHogTracker />
                      <AIAnalysisProvider>
                      <Suspense fallback={<PageLoader />}>
                      <Routes>
                        {/* Public routes */}
                        {/* Racine : redirige vers la landing dans la langue détectée (/fr, /en, /es, /it, /de, /pt). */}
                        <Route path="/" element={<RootLanguageRedirect />} />
                        {/* Routes déclarées une par une plutôt qu'en `/:lang` : un
                            paramètre dynamique capterait aussi les chemins d'un seul
                            segment inconnus et court-circuiterait la page 404. */}
                        {SUPPORTED_LANGUAGES.map(code => (
                          <Route key={code} path={`/${code}`} element={<LocalizedLandingRoute language={code} />} />
                        ))}
                        <Route path="/landing-beta" element={<LandingPageWrapper />} />
                        <Route path="/login" element={
                          <AuthRedirect>
                            <PublicPageWrapper><LoginForm /></PublicPageWrapper>
                          </AuthRedirect>
                        } />
                        <Route path="/en/login" element={<Navigate to="/login" replace />} />
                        <Route path="/admin-auth" element={<AdminAuth />} />
                        <Route path="/register" element={<PublicPageWrapper><RegisterForm /></PublicPageWrapper>} />
                        <Route path="/en/register" element={<Navigate to="/register" replace />} />
                        <Route path="/onboarding" element={
                          <OnboardingRoute>
                            <Onboarding />
                          </OnboardingRoute>
                        } />
                        <Route path="/email-verification-handler" element={<EmailVerificationHandler />} />
                        <Route path="/forgot-password" element={<PublicPageWrapper><ForgotPasswordForm /></PublicPageWrapper>} />
                        <Route path="/reset-password" element={<PublicPageWrapper><ResetPasswordForm /></PublicPageWrapper>} />
                        <Route path="/en/forgot-password" element={<Navigate to="/forgot-password" replace />} />
                        <Route path="/pricing" element={<PublicPageWrapper><Pricing /></PublicPageWrapper>} />
                        <Route path="/en/pricing" element={<Navigate to="/pricing" replace />} />
                        <Route path="/newpricingtemp" element={<PublicPageWrapper><PricingNew /></PublicPageWrapper>} />
                        <Route path="/direct-pricing" element={<PublicPageWrapper><DirectPricing /></PublicPageWrapper>} />
                        {/* Atterrissage des CTA de la séquence email (voir trialEmailSequence.cron.js) */}
                        <Route path="/offre" element={<PublicPageWrapper><Offer /></PublicPageWrapper>} />
                        <Route path="/verify-email" element={<PublicPageWrapper><EmailVerification /></PublicPageWrapper>} />
                        <Route path="/complete-registration/:token" element={<PublicPageWrapper><EmailValidationForm /></PublicPageWrapper>} />
                        <Route path="/contact" element={<PublicPageWrapper><ContactUs /></PublicPageWrapper>} />
                        {/* ContactUs sert désormais fr/en/es/it/de/pt via i18next (namespace `public`) ;
                            l'ancienne copie anglaise codée en dur est retirée, l'URL legacy redirige. */}
                        <Route path="/en/contact" element={<Navigate to="/contact" replace />} />
                        {/* Terms/Privacy/AffiliateTerms sont des documents contractuels déjà
                            rédigés uniquement en anglais (langue de référence unique, volontaire) :
                            pas de traduction, les anciennes copies /en/* codées en dur sont retirées. */}
                        <Route path="/terms" element={<PublicPageWrapper><TermsOfService /></PublicPageWrapper>} />
                        <Route path="/en/terms" element={<Navigate to="/terms" replace />} />
                        <Route path="/privacy" element={<PublicPageWrapper><PrivacyPolicy /></PublicPageWrapper>} />
                        <Route path="/en/privacy" element={<Navigate to="/privacy" replace />} />
                        <Route path="/security" element={<PublicPageWrapper><SecurityPolicy /></PublicPageWrapper>} />
                        <Route path="/en/security" element={<Navigate to="/security" replace />} />
                        <Route path="/affiliate-terms" element={<PublicPageWrapper><AffiliateTerms /></PublicPageWrapper>} />
                        <Route path="/en/affiliate-terms" element={<Navigate to="/affiliate-terms" replace />} />
                        <Route path="/success" element={<PublicPageWrapper><Success /></PublicPageWrapper>} />
                        <Route path="/welcome" element={<PublicPageWrapper><Welcome /></PublicPageWrapper>} />
                        <Route path="/welcome-a" element={<PublicPageWrapper><WelcomeA /></PublicPageWrapper>} />
                        <Route path="/welcome-m" element={<PublicPageWrapper><WelcomeM /></PublicPageWrapper>} />
                        <Route path="/features" element={<PublicPageWrapper><Features /></PublicPageWrapper>} />
                        <Route path="/en/features" element={<Navigate to="/features" replace />} />
                        <Route path="/academy" element={<PublicPageWrapper><Academy /></PublicPageWrapper>} />
                        <Route path="/academy/:id" element={<PublicPageWrapper><Academy /></PublicPageWrapper>} />
                        <Route path="/email-verification" element={<PublicPageWrapper><EmailVerification /></PublicPageWrapper>} />
                        <Route path="/email-verified" element={<PublicPageWrapper><EmailVerified /></PublicPageWrapper>} />
                        <Route path="/email-confirmed" element={<PublicPageWrapper><EmailConfirmed /></PublicPageWrapper>} />
                        <Route path="/academy-temp" element={<PublicPageWrapper><AcademyTemp /></PublicPageWrapper>} />
                        <Route path="/join-academy" element={<PublicPageWrapper><JoinAcademy /></PublicPageWrapper>} />
                        <Route path="/join-betatester" element={<PublicPageWrapper><JoinBetatester /></PublicPageWrapper>} />
                        <Route path="/shared/:shareId" element={<PublicPageWrapper><SharedTrades /></PublicPageWrapper>} />
                        <Route path="/changelog" element={<PublicPageWrapper><ChangelogPage /></PublicPageWrapper>} />
                        <Route path="/brokers" element={<PublicPageWrapper><BrokerList /></PublicPageWrapper>} />
                        <Route path="/en/brokers" element={<Navigate to="/brokers" replace />} />
                        <Route path="/plateformes" element={<PublicPageWrapper><BrokerList /></PublicPageWrapper>} />
                        {/* Disclaimer est un document de conformité déjà rédigé uniquement en
                            anglais (même choix que Terms/Privacy/AffiliateTerms) : pas de traduction. */}
                        <Route path="/disclaimer" element={<PublicPageWrapper><Disclaimer /></PublicPageWrapper>} />
                        <Route path="/en/disclaimer" element={<Navigate to="/disclaimer" replace />} />
                        <Route path="/backtesting" element={<PublicPageWrapper><BacktestingPage /></PublicPageWrapper>} />
                        <Route path="/en/backtesting" element={<Navigate to="/backtesting" replace />} />
                        <Route path="/affiliation" element={<PublicPageWrapper><AffiliatePage /></PublicPageWrapper>} />
                        <Route path="/en/affiliation" element={<Navigate to="/affiliation" replace />} />
                        <Route path="/affiliate" element={<PublicPageWrapper><AffiliatePage /></PublicPageWrapper>} />
                        <Route path="/affiliation/login" element={<PublicPageWrapper><AffiliateLogin /></PublicPageWrapper>} />
                        <Route path="/affiliation/register" element={<PublicPageWrapper><AffiliateRegister /></PublicPageWrapper>} />
                        <Route path="/affiliate/dashboard" element={
                          <PrivateRoute>
                            <AffiliateDashboard />
                          </PrivateRoute>
                        } />
                        <Route path="/partners/dashboard" element={
                          <PrivateRoute>
                            <PartnerDashboard />
                          </PrivateRoute>
                        } />
                        <Route path="/partners-program" element={<PublicPageWrapper><PartnersProgram /></PublicPageWrapper>} />
                        <Route path="/our-partners" element={<PublicPageWrapper><OurPartnersPage /></PublicPageWrapper>} />
                        <Route path="/en/our-partners" element={<Navigate to="/our-partners" replace />} />
                        <Route path="/partners/login" element={<PublicPageWrapper><PartnersLogin /></PublicPageWrapper>} />
                        <Route path="/partners/register" element={<PublicPageWrapper><PartnersRegister /></PublicPageWrapper>} />
                        <Route path="/zefrenchtrader" element={<PublicPageWrapper><ZeFrenchTrader /></PublicPageWrapper>} />
                        <Route path="/essai" element={<PublicPageWrapper><Essai /></PublicPageWrapper>} />
                        <Route path="/newsletter/unsubscribe" element={<PublicPageWrapper><NewsletterUnsubscribe /></PublicPageWrapper>} />
                        <Route path="/BlackFriday" element={<BlackFridayWrapper />} />
                        <Route path="/blackfriday-temp" element={<PublicPageWrapper><BlackFridayTemp /></PublicPageWrapper>} />
                        <Route path="/ctrader-logout" element={<CTraderLogout />} />
                        
                        {/* Help Center routes */}
                        <Route path="/help" element={<PublicPageWrapper><HelpCenter /></PublicPageWrapper>} />
                        <Route path="/help/:categoryId" element={<PublicPageWrapper><HelpCategory /></PublicPageWrapper>} />
                        <Route path="/help/:categoryId/:articleId" element={<PublicPageWrapper><HelpArticle /></PublicPageWrapper>} />

                        {/* Protected routes — Layout partagé : reste monté entre les navigations */}
                        <Route element={<PrivateRoute><TrialDemarrageGuard><Layout /></TrialDemarrageGuard></PrivateRoute>}>
                          {/* Routes accessibles à tous les utilisateurs authentifiés */}
                          <Route path="/demarrage" element={<Demarrage />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          {/* Anciennes URLs de la séance — conservées en redirect permanent. */}
                          <Route path="/session" element={<Navigate to="/seance" replace />} />
                          <Route path="/plan-vs-realite" element={<Navigate to="/seance" replace />} />
                          <Route path="/profile" element={<Profile />} />
                          {/* Centre RGPD — accessible à tout compte authentifié, sans
                              condition d'abonnement : l'exercice des droits ne se
                              monnaie pas (art. 12-5). */}
                          <Route path="/privacy-center" element={<PrivacyCenter />} />
                          {/* Un droit = une URL : /privacy-center/history,
                              /privacy-center/delete, /privacy-center/export… */}
                          <Route path="/privacy-center/:section" element={<PrivacyCenter />} />
                          <Route path="/new-trade" element={<NewTrade />} />
                          <Route path="/journal" element={<Journal />} />
                          <Route path="/position-calculator" element={<PositionCalculator />} />
                          <Route path="/analytics" element={<Analytics />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/partners" element={<Partners />} />
                          <Route path="/sucess" element={<BetaFeatureRoute><Achievements /></BetaFeatureRoute>} />
                          <Route path="/calendar" element={<Calendar />} />
                          <Route path="/recommandation" element={<Affiliate />} />
                          <Route path="/community" element={<Community />} />
                          <Route path="/leaderboard" element={<Leaderboard />} />
                          {/* Routes Pro uniquement */}
                          <Route path="/tags" element={<PrivateProRoute><Tags /></PrivateProRoute>} />
                          {/* Ma méthode — deux routes distinctes (état, quotas et collections
                              séparés) présentées comme une seule page à onglets. Plans est
                              ouvert à tous ; l'onglet Stratégies reste encore réservé aux
                              bêta-testeurs (badge « Soon » dans MethodeHeader). */}
                          <Route path="/methode/plans" element={<Plans />} />
                          <Route path="/methode/strategies" element={<BetaFeatureRoute><Playbook /></BetaFeatureRoute>} />
                          <Route path="/methode" element={<Navigate to="/methode/plans" replace />} />
                          <Route path="/plans" element={<Navigate to="/methode/plans" replace />} />
                          <Route path="/playbook" element={<Navigate to="/methode/strategies" replace />} />
                          <Route path="/retails" element={<PrivateProRoute><Retails /></PrivateProRoute>} />
                          <Route path="/notes" element={<PrivateProRoute><Notes /></PrivateProRoute>} />
                          <Route path="/backtest" element={<PrivatePremiumOnlyRoute><Backtest /></PrivatePremiumOnlyRoute>} />
                          <Route path="/seasonality" element={<PrivateProRoute><Seasonality /></PrivateProRoute>} />
                          <Route path="/ia-analysis" element={<PrivateProRoute><IAAnalysis /></PrivateProRoute>} />
                          <Route path="/seance" element={<PrivateProRoute><PlanVsRealite /></PrivateProRoute>} />
                          <Route path="/discipline" element={<PrivateProRoute><Discipline /></PrivateProRoute>} />
                          <Route path="/rapports" element={<PrivateProRoute><BetaFeatureRoute><Reports /></BetaFeatureRoute></PrivateProRoute>} />
                          {/* allowPremium pas encore passé : le lancement public aux comptes premium
                              n'est pas fait (voir PROPFIRM_PUBLIC_LAUNCH dans propFirmService.ts et
                              canAccessPropFirmBeta() dans firestore.rules, à flipper ensemble). */}
                          <Route path="/propfirm" element={<PrivateProRoute><BetaFeatureRoute><PropFirmCockpit /></BetaFeatureRoute></PrivateProRoute>} />
                          <Route path="/propfirm-challenges" element={<PrivateProRoute><BetaFeatureRoute><PropFirmChallenges /></BetaFeatureRoute></PrivateProRoute>} />
                          <Route path="/propfirm-simulator" element={<PrivateProRoute><BetaFeatureRoute><PropFirmSimulator /></BetaFeatureRoute></PrivateProRoute>} />
                          <Route path="/backtest/session/:sessionId" element={<PrivatePremiumOnlyRoute><BacktestSession /></PrivatePremiumOnlyRoute>} />
                          {/* Redactor / Admin */}
                          <Route path="/admin/blog" element={<RedactorRoute><AdminBlog /></RedactorRoute>} />
                          <Route path="/admin" element={<AdminRoute><AdminRoutes /></AdminRoute>} />
                          <Route path="/admin-stats" element={<AdminStatsPage />} />
                        </Route>

                        {/* Blog routes (public) */}
                        <Route path="/blog" element={<PublicPageWrapper><Blog /></PublicPageWrapper>} />
                        <Route path="/blog/:slug" element={<PublicPageWrapper><BlogArticle /></PublicPageWrapper>} />

                        {/* 404 - Page not found */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </AIAnalysisProvider>
                  {/* Modal d'annonce global */}
                  <Suspense fallback={null}><AnnouncementModalManager /></Suspense>
                  {/* Éditeur de thème custom (desktop overlay sur /dashboard) */}
                  <ThemeEditorManager />
                  {/* Modal d'onboarding affiliation */}
                  <Suspense fallback={null}><AffiliateOnboardingManager /></Suspense>
                  {/* Toggle API Mode (Local/Production) */}
                  <CookieConsentBanner />
                </ErrorBoundary>
                      </OnboardingDemoProvider>
                </Router>
                  </ToastProvider>
                </DisplayFormatProvider>
                </ActivationChecklistProvider>
                </AccountSyncProvider>
              </AccountProvider>
              </SimplifiedStatsProvider>
            </TradingAccountProvider>
          </UserPreferencesProvider>
        </SubscriptionProvider>
        </AnnouncementProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
